import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Plus,
  ArrowUpDown,
  Layers,
  Trash2,
  Keyboard,
  Calendar,
  UserPlus,
  List as ListIcon,
  Inbox,
  CheckSquare,
  MessageSquare,
  Paperclip,
  Repeat,
  Lock,
  Info,
} from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { canEditProtectedTaskFields } from "../../lib/taskPermissions";
import { LockedTaskField } from "./LockedTaskField";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { TEST_IDS } from "../../constants/testIds";
import { dateGroupOf, dueBadge } from "../../lib/dates";
import { nameInitials } from "../../lib/nameInitials";
import InlineAssigneePicker from "./InlineAssigneePicker";
import { CoAssigneeAvatars } from "./AssigneeAvatars";
import InlinePriorityPicker from "./InlinePriorityPicker";
import InlineDatePicker from "./InlineDatePicker";
import InlineRecurrencePicker from "./InlineRecurrencePicker";
import RichTextEditor from "./RichTextEditor";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";

const PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };
const GROUP_ORDER_DATE = ["overdue", "today", "this_week", "later", "no_date"];
const GROUP_ORDER_PRIORITY = ["urgent", "high", "medium", "low"];
const REC_STEPS = {
  daily: { amount: 1, unit: "day" },
  weekly: { amount: 1, unit: "week" },
  monthly: { amount: 1, unit: "month" },
  yearly: { amount: 1, unit: "year" },
};
const RECURRING_HORIZON_MONTHS = 12;
const PAGE_SIZE = 25;

const TONE_CLASS = {
  red: "bg-red-50 text-red-700 ring-red-100",
  amber: "bg-amber-50 text-amber-800 ring-amber-100",
  blue: "bg-blue-50 text-blue-700 ring-blue-100",
  zinc: "bg-zinc-100 text-zinc-700 ring-zinc-200",
};

function DueBadge({ task, tenantTz, onClick, testId }) {
  const { t } = useTranslation();
  if (!task.due_at) return null;
  const info = dueBadge(task.due_at, tenantTz, task.status);
  if (!info) return null;
  const label =
    info.key === "overdue"
      ? `${t("tasks.due_overdue")} ${info.daysAgo}g · ${info.timePart}`
      : info.key === "today"
        ? `${t("tasks.due_today")} ${info.timePart}`
        : info.key === "tomorrow"
          ? `${t("tasks.due_tomorrow")} ${info.timePart}`
          : info.key === "this_week"
            ? `${info.dateStr} ${info.timePart}`
            : info.key === "later"
              ? `${info.dateStr} · ${info.timePart}`
              : info.dateStr;
  // Render as <span> so it can safely be nested inside InlineDatePicker's
  // PopoverTrigger <button>. Radix will forward the click to the outer button.
  return (
    <span
      data-testid={testId}
      className={[
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ring-1 transition-colors hover:brightness-95",
        TONE_CLASS[info.tone] || TONE_CLASS.zinc,
      ].join(" ")}
    >
      <Calendar className="h-3 w-3" />
      {label}
    </span>
  );
}

/**
 * Render a task title, styling any `@Full Name` that matches a known
 * tenant user as underlined + bold (Slack-style mentions).
 */
function renderTitleWithMentions(title, users) {
  if (!title) return null;
  const names = users
    .map((u) => u.name)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length); // longest first for greedy match
  if (!names.length) return title;
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`@(${names.map(escape).join("|")})`, "g");
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(title)) !== null) {
    if (m.index > last) out.push(title.slice(last, m.index));
    out.push(
      <span
        key={`mn-${m.index}`}
        data-testid="row-mention"
        className="font-semibold underline decoration-1 underline-offset-2 decoration-zinc-500 text-zinc-900"
      >
        @{m[1]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < title.length) out.push(title.slice(last));
  return out.length ? out : title;
}

function Avatar({ name }) {
  if (!name) return null;
  return (
    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 text-[10px] font-semibold text-white ring-2 ring-white">
      {nameInitials(name)}
    </div>
  );
}

/**
 * TaskRow — a single Linear/Slack-inspired row with inline pickers,
 * hover quick actions and a "completing" slide-out animation.
 */
function TaskRow({
  task,
  users,
  tenantTz,
  lists,
  userMap,
  taskReads,
  focused,
  completing,
  currentUser,
  onOpen,
  onToggleDone,
  onUpdate,
  onDelete,
  onFocus,
}) {
  // Row-level RBAC — same predicate the backend enforces in
  // PATCH /tasks/{id}. Non-admin users can only complete /
  // (re)assign / reschedule tasks they own or are assigned to.
  const canEdit = canEditProtectedTaskFields(currentUser, task);
  const canReassign = canEdit;
  const readAt = taskReads[task.id];
  const hasUnread =
    !readAt || (task.updated_at && new Date(task.updated_at) > new Date(readAt));
  const list = lists.find((l) => l.id === task.list_id);
  const doneSubs = (task.subtasks || []).filter((s) => s.done).length;
  const totalSubs = (task.subtasks || []).length;
  const commentCount = task.comment_count || 0;
  const attachmentCount = task.attachment_count || 0;

  const openTab = (e, tabKey) => {
    e.stopPropagation();
    onOpen(task, tabKey);
  };

  return (
    <li
      data-testid={TEST_IDS.taskRow(task.id)}
      onMouseEnter={onFocus}
      onClick={() => onOpen(task)}
      className={[
        "group flex flex-wrap items-center gap-2 px-3 py-2 sm:grid sm:grid-cols-[auto_auto_1fr_auto_auto_auto_auto_auto] sm:gap-3 sm:px-5 sm:py-2.5 cursor-pointer",
        "border-l-2 transition-all duration-200",
        task._virtual
          ? "border-purple-300 bg-purple-50/40 hover:bg-purple-50/70"
          : focused
            ? "border-zinc-900 bg-zinc-50"
            : "border-transparent hover:bg-zinc-50",
        completing ? "opacity-0 -translate-x-2" : "opacity-100",
      ].join(" ")}
      style={{ transitionProperty: "opacity, transform, background-color, border-color" }}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <LockedTaskField
          locked={!canEdit}
          reason="complete"
          showBadge={!canEdit}
          taskId={task.id}
        >
          <Checkbox
            checked={task.status === "done"}
            onCheckedChange={() => onToggleDone(task)}
            disabled={!canEdit}
            data-testid={TEST_IDS.taskCheckbox(task.id)}
          />
        </LockedTaskField>
      </div>
      {hasUnread ? (
        <span
          data-testid={TEST_IDS.taskUnreadDot(task.id)}
          className="h-1.5 w-1.5 rounded-full bg-blue-600"
        />
      ) : (
        <span className="h-1.5 w-1.5" />
      )}
      <div className="min-w-0 flex-1 basis-full sm:basis-auto" data-testid={TEST_IDS.taskTitle(task.id)}>
        <div
          className={[
            "truncate text-sm font-medium",
            task.status === "done"
              ? "text-zinc-400 line-through"
              : "text-zinc-900",
          ].join(" ")}
        >
          {task.visibility && task.visibility !== "everyone" && (
            <Lock
              className="mr-1 inline h-3 w-3 -translate-y-px text-amber-500"
              data-testid={`task-private-${task.id}`}
              aria-label="Gizli görev"
            />
          )}
          {renderTitleWithMentions(task.title, users)}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-500">
          {task._pending && (
            <span
              className="inline-flex items-center gap-1 whitespace-nowrap rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700"
              title="Sunucuya gönderilmeyi bekliyor"
              data-testid={`task-pending-${task.id}`}
            >
              ⏳ Bekliyor
            </span>
          )}
          {list && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded bg-zinc-100 px-1.5 py-0.5 text-zinc-600">
              <Layers className="h-2.5 w-2.5" />
              {list.name}
            </span>
          )}
          {totalSubs > 0 && (
            <button
              type="button"
              onClick={(e) => openTab(e, "subtasks")}
              data-testid={`task-icon-subtasks-${task.id}`}
              title="Alt görevler"
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors tabular-nums"
            >
              <CheckSquare className="h-3 w-3" />
              {doneSubs}/{totalSubs}
            </button>
          )}
          {commentCount > 0 && (
            <button
              type="button"
              onClick={(e) => openTab(e, "comments")}
              data-testid={`task-icon-comments-${task.id}`}
              title="Yorumlar"
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors tabular-nums"
            >
              <MessageSquare className="h-3 w-3" />
              {commentCount}
            </button>
          )}
          {attachmentCount > 0 && (
            <button
              type="button"
              onClick={(e) => openTab(e, "files")}
              data-testid={`task-icon-files-${task.id}`}
              title="Dosyalar"
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors tabular-nums"
            >
              <Paperclip className="h-3 w-3" />
              {attachmentCount}
            </button>
          )}
          <InlineRecurrencePicker
            recurrence={task.recurrence || "none"}
            testId={`inline-recurrence-${task.id}`}
            onSelect={(v) => onUpdate(task.id, { recurrence: v })}
          />
        </div>
      </div>
      <div className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
        {!task.due_at && canEdit && (
          <InlineDatePicker
            dueAt={task.due_at}
            tenantTz={tenantTz}
            onChange={(iso) => onUpdate(task.id, { due_at: iso })}
            testId={TEST_IDS.taskRowDateBtn(task.id)}
          />
        )}
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <InlinePriorityPicker
          priority={task.priority}
          testId={`inline-priority-${task.id}`}
          onSelect={(p) => onUpdate(task.id, { priority: p })}
        />
      </div>
      <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1">
        <InlineAssigneePicker
          assigneeId={task.assignee_id}
          users={users}
          testId={`inline-assignee-${task.id}`}
          canReassign={canReassign}
          currentUserId={currentUser?.id}
          taskTitle={task.title}
          onSelect={(uid) => onUpdate(task.id, { assignee_id: uid })}
        />
        <CoAssigneeAvatars task={task} users={users} />
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        {task.due_at ? (
          canEdit ? (
            <InlineDatePicker
              dueAt={task.due_at}
              tenantTz={tenantTz}
              onChange={(iso) => onUpdate(task.id, { due_at: iso })}
              testId={TEST_IDS.taskRowDateInput(task.id)}
              triggerClassName="inline-flex"
            >
              <DueBadge
                task={task}
                tenantTz={tenantTz}
                testId={`task-due-badge-${task.id}`}
              />
            </InlineDatePicker>
          ) : (
            <LockedTaskField locked reason="reschedule" taskId={task.id}>
              <DueBadge
                task={task}
                tenantTz={tenantTz}
                testId={`task-due-badge-${task.id}`}
              />
            </LockedTaskField>
          )
        ) : (
          <span className="h-5 w-5" />
        )}
      </div>
      <button
        type="button"
        data-testid={TEST_IDS.taskDeleteBtn(task.id)}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(task);
        }}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-red-600"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
}

function GroupHeader({ id, label, count, collapsed, onToggle }) {
  return (
    <button
      type="button"
      data-testid={TEST_IDS.listGroupHeader(id)}
      onClick={onToggle}
      className="flex w-full items-center gap-2 px-5 py-2 text-left hover:bg-zinc-50/60 transition-colors"
    >
      {collapsed ? (
        <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
      ) : (
        <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
      )}
      <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-600">
        {label}
      </span>
      <span className="text-[11px] text-zinc-400">{count}</span>
    </button>
  );
}

/**
 * TaskListView — the professional list view with grouping, sorting,
 * keyboard navigation, show/hide completed, hover quick actions,
 * completion animation + undo toast.
 */
export default function TaskListView({
  tasks,
  lists,
  users,
  tenantTz,
  projectId,
  currentUser,
  sortBy,
  groupBy,
  showCompleted,
  showRecurring,
  overdueOnly = false,
  activeListId = null,
  openComposerToken = 0,
  onOverdueChange,
  onSortByChange,
  onGroupByChange,
  onShowCompletedChange,
  onShowRecurringChange,
  onOpen,
  onUpdate,
  onDelete,
  onCreate,
}) {
  const { t } = useTranslation();
  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
  const baseTasksById = useMemo(
    () => Object.fromEntries(tasks.map((t) => [t.id, t])),
    [tasks],
  );
  const resolveBase = (t) => (t?._base_id ? baseTasksById[t._base_id] || t : t);
  const [taskReads, setTaskReads] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [focusedIdx, setFocusedIdx] = useState(0);
  const [completingId, setCompletingId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newListId, setNewListId] = useState(activeListId || lists[0]?.id || "");
  const [newAssigneeId, setNewAssigneeId] = useState("__none__");
  const [newVisibility, setNewVisibility] = useState("everyone");
  const [newVisibleToIds, setNewVisibleToIds] = useState([]);
  const [peoplePickerOpen, setPeoplePickerOpen] = useState(false);
  const [peopleQuery, setPeopleQuery] = useState("");
  const [assigneeInfoOpen, setAssigneeInfoOpen] = useState(false);
  const [newMentionIds, setNewMentionIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  // Local "sadece tamamlanan" flag — combined with parent's `showCompleted`
  // to power the segmented control. When true we render *only* done tasks
  // (i.e. the "Tamamlandı" chip is active).
  const [doneOnly, setDoneOnly] = useState(false);

  useEffect(() => {
    if (!newListId && lists[0]) setNewListId(lists[0].id);
  }, [lists, newListId]);

  // Whenever the user switches the current group filter (URL `?list=…`) or opens
  // the composer, sync the "add to list" picker so tasks land in the group the
  // user is actually viewing. Avoids the classic "Backlog"-always-selected bug.
  useEffect(() => {
    if (activeListId) setNewListId(activeListId);
  }, [activeListId]);

  // Allow parent (e.g. ProjectDetail's "Görev/not ekle" button next to the
  // tabs) to open the composer imperatively. We watch a monotonically-
  // increasing token so the same trigger fires each click.
  useEffect(() => {
    if (openComposerToken > 0) {
      setAddOpen(true);
      if (activeListId) setNewListId(activeListId);
      setNewAssigneeId("__none__");
      setNewMentionIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openComposerToken]);

  // Reset pagination when filters that change flat count are toggled
  useEffect(() => {
    setCurrentPage(0);
  }, [sortBy, groupBy, showCompleted, showRecurring, tasks.length]);

  // Filter completed if hidden, then optionally expand recurring instances
  const visibleTasks = useMemo(() => {
    let base = tasks;
    if (doneOnly) {
      // "Tamamlandı" chip active — show only done tasks (overrides showCompleted).
      base = base.filter((t) => t.status === "done");
    } else if (!showCompleted) {
      base = base.filter((t) => t.status !== "done");
    }
    if (!showRecurring) return base;
    // Expand recurring tasks into virtual instances up to the horizon.
    const horizonEnd = dayjs().add(RECURRING_HORIZON_MONTHS, "month");
    const out = [];
    base.forEach((t) => {
      out.push(t);
      const rec = t.recurrence || "none";
      if (rec === "none" || !t.due_at) return;
      const step = REC_STEPS[rec];
      if (!step) return;
      let cur = dayjs(t.due_at);
      for (let i = 0; i < 500; i++) {
        cur = cur.add(step.amount, step.unit);
        if (cur.isAfter(horizonEnd)) break;
        out.push({
          ...t,
          id: `${t.id}__occ_${cur.valueOf()}`,
          _base_id: t.id,
          _virtual: true,
          due_at: cur.toISOString(),
        });
      }
    });
    return out;
  }, [tasks, showCompleted, showRecurring, doneOnly]);

  // Sorting comparator
  const sortComparator = useMemo(() => {
    switch (sortBy) {
      case "deadline":
        return (a, b) => {
          if (!a.due_at && !b.due_at) return 0;
          if (!a.due_at) return 1;
          if (!b.due_at) return -1;
          return new Date(a.due_at) - new Date(b.due_at);
        };
      case "priority":
        return (a, b) =>
          (PRIORITY_RANK[a.priority] ?? 4) - (PRIORITY_RANK[b.priority] ?? 4);
      case "assignee":
        return (a, b) => {
          const an = userMap[a.assignee_id]?.name || "\uFFFF";
          const bn = userMap[b.assignee_id]?.name || "\uFFFF";
          return an.localeCompare(bn);
        };
      case "created":
        return (a, b) => new Date(b.created_at) - new Date(a.created_at);
      case "manual":
      default:
        return null; // preserve incoming order
    }
  }, [sortBy, userMap]);

  // Build groups
  const groups = useMemo(() => {
    const buckets = {};
    for (const task of visibleTasks) {
      let key;
      if (groupBy === "date") key = dateGroupOf(task.due_at, tenantTz, task.status);
      else if (groupBy === "list") key = task.list_id || "no_list";
      else if (groupBy === "priority") key = task.priority || "medium";
      else key = "all";
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(task);
    }
    if (sortComparator) {
      for (const k of Object.keys(buckets)) {
        buckets[k] = [...buckets[k]].sort(sortComparator);
      }
    }
    let order = [];
    if (groupBy === "date") order = GROUP_ORDER_DATE.filter((k) => buckets[k]);
    else if (groupBy === "priority")
      order = GROUP_ORDER_PRIORITY.filter((k) => buckets[k]);
    else if (groupBy === "list")
      order = lists.map((l) => l.id).filter((k) => buckets[k]);
    else order = Object.keys(buckets);
    return { buckets, order };
  }, [visibleTasks, groupBy, sortComparator, tenantTz, lists]);

  // Flattened visible task list for keyboard nav + pagination
  const flatTasks = useMemo(() => {
    const out = [];
    for (const k of groups.order) {
      if (collapsed[k]) continue;
      for (const task of groups.buckets[k]) out.push(task);
    }
    return out;
  }, [groups, collapsed]);

  const totalPages = Math.max(1, Math.ceil(flatTasks.length / PAGE_SIZE));
  const pageStart = Math.min(currentPage * PAGE_SIZE, Math.max(0, flatTasks.length - 1));
  const pageEnd = pageStart + PAGE_SIZE;
  const paginatedFlat = useMemo(
    () => flatTasks.slice(pageStart, pageEnd),
    [flatTasks, pageStart, pageEnd],
  );
  const paginatedIdSet = useMemo(
    () => new Set(paginatedFlat.map((t) => t.id)),
    [paginatedFlat],
  );

  // Re-group only the paginated slice so each page still shows headers
  const paginatedGroups = useMemo(() => {
    const buckets = {};
    for (const k of groups.order) {
      const filtered = (groups.buckets[k] || []).filter((t) =>
        paginatedIdSet.has(t.id),
      );
      if (filtered.length) buckets[k] = filtered;
    }
    return { buckets, order: Object.keys(buckets) };
  }, [groups, paginatedIdSet]);

  useEffect(() => {
    if (focusedIdx >= paginatedFlat.length)
      setFocusedIdx(Math.max(0, paginatedFlat.length - 1));
  }, [paginatedFlat.length, focusedIdx]);

  // Mark-read fetch
  useEffect(() => {
    let cancelled = false;
    import("../../lib/api").then(({ api }) => {
      api.get("/users/me/task-reads").then(({ data }) => {
        if (!cancelled) setTaskReads(data || {});
      }).catch(() => {});
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleToggleDone = async (task) => {
    if (task.status === "done") {
      // Uncomplete immediately
      await onUpdate(task.id, { status: "todo" });
      return;
    }
    // Animate + undo
    setCompletingId(task.id);
    setTimeout(async () => {
      await onUpdate(task.id, { status: "done" });
      setCompletingId(null);
      toast.custom(
        (toastId) => (
          <div
            data-testid="task-completed-toast"
            className="flex items-center gap-3 rounded-lg bg-zinc-900 px-4 py-3 text-sm text-white shadow-xl ring-1 ring-white/10"
          >
            <span className="font-medium">{t("tasks.task_completed")}</span>
            <span className="text-zinc-400">·</span>
            <span className="text-zinc-300 truncate max-w-[220px]">{task.title}</span>
            <button
              type="button"
              data-testid={TEST_IDS.undoTaskComplete}
              onClick={() => {
                onUpdate(task.id, { status: "todo" });
                toast.dismiss(toastId);
              }}
              className="ml-1 rounded-md bg-white/10 px-2.5 py-1 text-xs font-medium text-white hover:bg-white/20 transition-colors"
            >
              {t("tasks.undo")}
            </button>
          </div>
        ),
        { duration: 5000 },
      );
    }, 380);
  };

  // Keyboard nav — attached to window
  useEffect(() => {
    const handler = (e) => {
      // Ignore when typing in an input, textarea, contentEditable or when a popover/dialog is open
      const active = document.activeElement;
      if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable)) return;
      if (document.querySelector('[data-state="open"][role="dialog"]')) return;
      // Skip when the quick-add composer is open (title / description / pickers
      // may steal focus briefly between clicks — global shortcuts would fire).
      if (addOpen) return;
      if (!paginatedFlat.length) return;
      const cur = paginatedFlat[focusedIdx];
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        setFocusedIdx((i) => Math.min(paginatedFlat.length - 1, i + 1));
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        setFocusedIdx((i) => Math.max(0, i - 1));
      } else if (e.key === " " && cur) {
        e.preventDefault();
        handleToggleDone(cur);
      } else if ((e.key === "e" || e.key === "E" || e.key === "Enter") && cur) {
        e.preventDefault();
        onOpen(cur);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedFlat, focusedIdx, addOpen]);

  const groupLabel = (k) => {
    if (groupBy === "date") return t(`tasks.group_${k}`);
    if (groupBy === "list") return lists.find((l) => l.id === k)?.name || "—";
    if (groupBy === "priority") return t(`tasks.priority_${k}`);
    return k;
  };

  const submitQuickAdd = async (e) => {
    e?.preventDefault?.();
    // Body is markdown (may include newlines, formatting, mentions).
    // Title = first non-empty line, stripped of markdown emphasis;
    // Description = the rest (kept as markdown so bold/lists/links survive).
    const raw = (newTitle || "").trim();
    if (!raw || !newListId) return;
    // Guardrail: user typed ONLY an @mention (e.g. "@owner") with no actual
    // task description — that produced tasks whose only title was "@owner".
    // Require real content besides mentions before we accept the submit.
    const withoutMentions = raw.replace(/@[\p{L}\p{N}._-]+/gu, "").trim();
    if (!withoutMentions) {
      toast.warning(t("tasks.mention_only_warning"));
      return;
    }
    const lines = raw.split(/\r?\n/);
    let firstIdx = lines.findIndex((l) => l.trim().length > 0);
    if (firstIdx === -1) return;
    const firstLine = lines[firstIdx];
    // Strip common markdown emphasis so the title reads cleanly in list rows.
    const cleanTitle = firstLine
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/\*(.+?)\*/g, "$1")
      .replace(/`(.+?)`/g, "$1")
      .replace(/~~(.+?)~~/g, "$1")
      .replace(/\[(.+?)\]\((.+?)\)/g, "$1")
      .trim();
    const rest = lines.slice(firstIdx + 1).join("\n").trim();
    const payload = {
      project_id: projectId,
      list_id: newListId,
      title: cleanTitle,
    };
    if (rest) payload.description = rest;
    // Any @mention picks captured via the composer become assignees.
    // The explicit "Assign" Select acts as a fallback / override when
    // no mention was used.
    const mentionAssignees = Array.from(new Set(newMentionIds));
    if (mentionAssignees.length > 0) {
      payload.assignee_ids = mentionAssignees;
      payload.assignee_id = mentionAssignees[0];
    } else if (newAssigneeId && newAssigneeId !== "__none__") {
      payload.assignee_id = newAssigneeId;
    }
    if (newVisibility !== "everyone") payload.visibility = newVisibility;
    if (newVisibility === "custom" && newVisibleToIds.length > 0)
      payload.visible_to_ids = newVisibleToIds;
    await onCreate(payload);
    setNewTitle("");
    setNewMentionIds([]);
    setNewAssigneeId("__none__");
    setNewVisibility("everyone");
    setNewVisibleToIds([]);
    setPeopleQuery("");
  };

  return (
    <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-1.5 border-b border-zinc-100 px-3 py-2 sm:px-5">
        <div className="flex flex-wrap items-center gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid={TEST_IDS.listGroupToggle}
                className="h-7 gap-1 px-2 text-[11px] border-zinc-200 text-zinc-600"
              >
                <Layers className="h-3 w-3" />
                {t("tasks.group_by")}: {t(`tasks.group_by_${groupBy}`)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40">
              <DropdownMenuLabel>{t("tasks.group_by")}</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={groupBy} onValueChange={onGroupByChange}>
                <DropdownMenuRadioItem
                  value="date"
                  data-testid={TEST_IDS.listGroupOption("date")}
                >
                  {t("tasks.group_by_date")}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem
                  value="list"
                  data-testid={TEST_IDS.listGroupOption("list")}
                >
                  {t("tasks.group_by_list")}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem
                  value="priority"
                  data-testid={TEST_IDS.listGroupOption("priority")}
                >
                  {t("tasks.group_by_priority")}
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid={TEST_IDS.listSortMenu}
                className="h-7 gap-1 px-2 text-[11px] border-zinc-200 text-zinc-600"
              >
                <ArrowUpDown className="h-3 w-3" />
                {t("tasks.sort_by")}: {t(`tasks.sort_${sortBy}`)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuLabel>{t("tasks.sort_by")}</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={sortBy} onValueChange={onSortByChange}>
                {["manual", "deadline", "priority", "assignee", "created"].map((k) => (
                  <DropdownMenuRadioItem
                    key={k}
                    value={k}
                    data-testid={TEST_IDS.listSortOption(k)}
                  >
                    {t(`tasks.sort_${k}`)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* iter50: Segmented status chips + independent "Tekrar" toggle.
              Radio-behaviour for the first four (Tümü / Bekliyor / Tamamlandı /
              Gecikmiş) so exactly one is active at a time. */}
          {(() => {
            let mode = "open"; // Bekliyor
            if (doneOnly) mode = "done";
            else if (overdueOnly) mode = "overdue";
            else if (showCompleted) mode = "all";
            const chips = [
              { key: "all", label: "Tümü", tone: "zinc" },
              { key: "open", label: "Bekliyor", tone: "sky" },
              { key: "done", label: "Tamamlandı", tone: "emerald" },
              { key: "overdue", label: "Gecikmiş", tone: "red" },
            ];
            const applyMode = (k) => {
              // Radio: whichever chip is picked wins. We drive the parent
              // state via the provided callbacks + local doneOnly flag.
              if (k === "all") {
                setDoneOnly(false);
                onShowCompletedChange?.(true);
                onOverdueChange?.(false);
              } else if (k === "open") {
                setDoneOnly(false);
                onShowCompletedChange?.(false);
                onOverdueChange?.(false);
              } else if (k === "done") {
                setDoneOnly(true);
                onShowCompletedChange?.(true);
                onOverdueChange?.(false);
              } else if (k === "overdue") {
                setDoneOnly(false);
                onShowCompletedChange?.(false);
                onOverdueChange?.(true);
              }
            };
            const toneClasses = {
              zinc: "bg-zinc-900 text-white",
              sky: "bg-sky-600 text-white",
              emerald: "bg-emerald-600 text-white",
              red: "bg-red-600 text-white",
            };
            return (
              <>
                {chips.map((c) => {
                  const active = mode === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => applyMode(c.key)}
                      data-testid={`list-filter-chip-${c.key}`}
                      className={[
                        "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                        active
                          ? toneClasses[c.tone]
                          : "text-zinc-600 hover:bg-zinc-100",
                      ].join(" ")}
                    >
                      {c.label}
                    </button>
                  );
                })}
                {/* Independent toggle — recurrence is orthogonal to status. */}
                <button
                  type="button"
                  onClick={() => onShowRecurringChange(!showRecurring)}
                  data-testid="list-filter-chip-recurring"
                  className={[
                    "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                    showRecurring
                      ? "bg-violet-600 text-white"
                      : "text-zinc-600 hover:bg-zinc-100",
                  ].join(" ")}
                >
                  <Repeat className="h-3 w-3" strokeWidth={2} />
                  Tekrar
                </button>
              </>
            );
          })()}
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <span className="hidden lg:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-400">
            <Keyboard className="h-3 w-3" />
            {t("tasks.keyboard_hint")}
          </span>
        </div>
      </div>

      {/* Composer */}
      {addOpen && (
        lists.length === 0 ? (
          <div className="border-b border-zinc-100 bg-amber-50/60 px-5 py-4 flex items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <Inbox className="h-4 w-4 text-amber-700 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-amber-900">
                  {t("tasks.no_lists_title")}
                </div>
                <div className="mt-0.5 text-xs text-amber-800/80">
                  {t("tasks.no_lists_body")}
                </div>
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setAddOpen(false);
                setNewTitle("");
              }}
              className="text-amber-900 hover:bg-amber-100"
            >
              {t("projects.cancel")}
            </Button>
          </div>
        ) : (
          <form
            onSubmit={submitQuickAdd}
            className="border-b border-zinc-100 bg-zinc-50/60 px-5 py-3"
          >
            {/* Slack-style single-box composer. One rich-text editor (with the
                full toolbar: bold, italic, link, list, quote, code, emoji, @).
                Enter submits, Shift+Enter inserts a newline. First non-empty
                line becomes the task title, the rest becomes the markdown
                description. @mention auto-fills the assignee picker below. */}
            <div className="rounded-lg border border-zinc-300 bg-white shadow-sm focus-within:border-zinc-500 focus-within:ring-1 focus-within:ring-zinc-500 overflow-hidden">
              <RichTextEditor
                value={newTitle}
                onChange={setNewTitle}
                users={users}
                onMention={(uid) => {
                  setNewMentionIds((cur) => (cur.includes(uid) ? cur : [...cur, uid]));
                  // Mirror the first mention into the explicit assignee picker
                  // so the user visually sees who the task will be assigned to.
                  setNewAssigneeId((cur) =>
                    cur === "__none__" || !cur ? uid : cur,
                  );
                }}
                onSubmit={() => submitQuickAdd()}
                placeholder={t("lists.add_task") + " · Enter ile ekle · Shift+Enter yeni satır · @isim ile ata"}
                rows={2}
                bordered={false}
                autoFocus
                data-testid={TEST_IDS.listAddTaskInput}
              />

              {/* Footer: pickers on the left, actions on the right */}
              <div className="flex flex-wrap items-center gap-2 border-t border-zinc-100 bg-zinc-50/70 px-2 py-2">
                <Select value={newListId} onValueChange={setNewListId}>
                  <SelectTrigger
                    data-testid={TEST_IDS.listAddTaskListSelect}
                    className="h-8 w-40 border-zinc-200 bg-white text-xs"
                    title={t("tasks.select_list")}
                  >
                    <div className="flex items-center gap-1.5 text-zinc-600">
                      <ListIcon className="h-3.5 w-3.5 shrink-0" />
                      <SelectValue placeholder={t("tasks.select_list")} />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {lists.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={newAssigneeId} onValueChange={setNewAssigneeId}>
                  <SelectTrigger
                    data-testid="list-add-task-assignee-select"
                    className="h-8 w-40 border-zinc-200 bg-white text-xs"
                    title={t("tasks.select_assignee")}
                  >
                    <div className="flex items-center gap-1.5 text-zinc-600">
                      <UserPlus className="h-3.5 w-3.5 shrink-0" />
                      <SelectValue placeholder={t("tasks.select_assignee")} />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("tasks.unassigned")}</SelectItem>
                    {users
                      .filter((u) => u.active !== false)
                      .map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>

                <Select
                  value={newVisibility}
                  onValueChange={(v) => {
                    setNewVisibility(v);
                    if (v === "custom") {
                      // Radix Select kapanışı bittikten sonra kişi seçiciyi aç.
                      setTimeout(() => setPeoplePickerOpen(true), 150);
                    } else {
                      setNewVisibleToIds([]);
                      setPeoplePickerOpen(false);
                    }
                  }}
                >
                  <SelectTrigger
                    data-testid="list-add-task-visibility-select"
                    className="h-8 w-44 border-zinc-200 bg-white text-xs"
                    title="Kimler görebilir"
                  >
                    <div className="flex items-center gap-1.5 text-zinc-600">
                      <Lock className="h-3.5 w-3.5 shrink-0" />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="everyone">Herkes</SelectItem>
                    <SelectItem value="private">Sadece Ben</SelectItem>
                    <SelectItem value="participants">Sadece Atanmışlar</SelectItem>
                    <SelectItem value="custom">Seçili kişiler</SelectItem>
                  </SelectContent>
                </Select>

                {Array.from(new Set(newMentionIds)).length > 1 && (
                  <Popover open={assigneeInfoOpen} onOpenChange={setAssigneeInfoOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="list-add-task-multi-assignee-info"
                        title="Birden fazla kişi atandı — görmek için tıkla"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-violet-600 transition-colors hover:bg-violet-50"
                      >
                        <Info className="h-4 w-4 animate-pulse" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-60 p-2"
                      data-testid="multi-assignee-popover"
                    >
                      <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                        Atanan kişiler ({Array.from(new Set(newMentionIds)).length})
                      </p>
                      <ul className="flex flex-col gap-0.5">
                        {Array.from(new Set(newMentionIds)).map((id, i) => {
                          const u = users.find((x) => x.id === id);
                          return (
                            <li
                              key={id}
                              className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm text-zinc-800"
                              data-testid={`multi-assignee-row-${id}`}
                            >
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-violet-100 text-[10px] font-semibold text-violet-700">
                                {nameInitials(u?.name || "?")}
                              </span>
                              <span className="truncate">{u?.name || "?"}</span>
                              {i === 0 && (
                                <span className="ml-auto rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500">
                                  Birincil
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </PopoverContent>
                  </Popover>
                )}

                {newVisibility === "custom" && (
                  <Popover open={peoplePickerOpen} onOpenChange={setPeoplePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="list-add-task-people-btn"
                        className="h-8 gap-1 border-amber-300 bg-amber-50 px-2 text-xs text-amber-800 hover:bg-amber-100"
                      >
                        <UserPlus className="h-3.5 w-3.5" />
                        {newVisibleToIds.length > 0
                          ? `${newVisibleToIds.length} kişi seçili`
                          : "Kişi seç"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-64 p-0"
                      data-testid="visibility-people-popover"
                    >
                      <div className="border-b border-zinc-100 p-2">
                        <Input
                          value={peopleQuery}
                          onChange={(e) => setPeopleQuery(e.target.value)}
                          placeholder="Kişi ara…"
                          className="h-8 text-xs"
                          autoFocus
                          data-testid="visibility-people-search"
                        />
                      </div>
                      <div className="max-h-56 overflow-y-auto p-1">
                        {users
                          .filter((u) => u.active !== false)
                          .filter((u) =>
                            (u.name || "")
                              .toLocaleLowerCase("tr")
                              .includes(peopleQuery.toLocaleLowerCase("tr")),
                          )
                          .sort((a, b) =>
                            (a.name || "").localeCompare(b.name || "", "tr"),
                          )
                          .map((u) => (
                            <label
                              key={u.id}
                              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50"
                              data-testid={`visibility-people-option-${u.id}`}
                            >
                              <Checkbox
                                checked={newVisibleToIds.includes(u.id)}
                                onCheckedChange={(v) =>
                                  setNewVisibleToIds((cur) =>
                                    v
                                      ? [...cur, u.id]
                                      : cur.filter((x) => x !== u.id),
                                  )
                                }
                              />
                              <span className="truncate">{u.name}</span>
                            </label>
                          ))}
                      </div>
                      <div className="flex justify-end border-t border-zinc-100 p-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setPeoplePickerOpen(false)}
                          data-testid="visibility-people-done"
                          className="h-7 bg-zinc-900 px-3 text-xs text-white hover:bg-zinc-800"
                        >
                          Tamam
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                <div className="ml-auto flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setAddOpen(false);
                      setNewTitle("");
                      setNewMentionIds([]);
                      setNewAssigneeId("__none__");
                      setNewVisibility("everyone");
                      setNewVisibleToIds([]);
                      setPeopleQuery("");
                    }}
                  >
                    {t("projects.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    data-testid={TEST_IDS.listAddTaskSubmit}
                    className="bg-zinc-900 text-white hover:bg-zinc-800"
                  >
                    {t("common.add")}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )
      )}

      {/* Groups */}
      {flatTasks.length === 0 ? (
        <div className="px-5 py-16 text-center text-sm text-zinc-500">
          {t("tasks.no_tasks")}
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {paginatedGroups.order.map((k) => (
            <div key={k}>
              <GroupHeader
                id={k}
                label={groupLabel(k)}
                count={paginatedGroups.buckets[k].length}
                collapsed={!!collapsed[k]}
                onToggle={() =>
                  setCollapsed((s) => ({ ...s, [k]: !s[k] }))
                }
              />
              {!collapsed[k] && (
                <ul className="divide-y divide-zinc-100">
                  {paginatedGroups.buckets[k].map((task) => {
                    const idx = paginatedFlat.findIndex((tk) => tk.id === task.id);
                    const isVirtual = !!task._virtual;
                    return (
                      <TaskRow
                        key={task.id}
                        task={task}
                        users={users}
                        userMap={userMap}
                        tenantTz={tenantTz}
                        lists={lists}
                        taskReads={taskReads}
                        currentUser={currentUser}
                        focused={idx === focusedIdx}
                        completing={completingId === task.id}
                        onOpen={(_, tab) => onOpen(resolveBase(task), tab)}
                        onToggleDone={
                          isVirtual
                            ? () => onOpen(resolveBase(task))
                            : handleToggleDone
                        }
                        onUpdate={
                          isVirtual
                            ? () => onOpen(resolveBase(task))
                            : onUpdate
                        }
                        onDelete={
                          isVirtual
                            ? () => onOpen(resolveBase(task))
                            : onDelete
                        }
                        onFocus={() => setFocusedIdx(idx)}
                      />
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {flatTasks.length > PAGE_SIZE && (
        <div
          data-testid="list-pagination"
          className="flex items-center justify-between border-t border-zinc-100 bg-zinc-50/60 px-5 py-3"
        >
          <span className="text-xs text-zinc-600 tabular-nums">
            {t("tasks.pagination_range", {
              from: flatTasks.length === 0 ? 0 : pageStart + 1,
              to: Math.min(pageEnd, flatTasks.length),
              total: flatTasks.length,
            })}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              data-testid="list-pagination-prev"
              className="gap-1 border-zinc-200"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {t("tasks.pagination_prev")}
            </Button>
            <span
              className="mx-2 text-xs tabular-nums text-zinc-700"
              data-testid="list-pagination-current"
            >
              {currentPage + 1} / {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
              data-testid="list-pagination-next"
              className="gap-1 border-zinc-200"
            >
              {t("tasks.pagination_next")}
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
